import { useEffect, useState } from 'react';
import axios from 'axios';
import { Check, Loader2 } from 'lucide-react';
import { useSeitenaufruf } from '@/hooks/useSeitenaufruf';

/* E14 Kalorienrechner als Lead-Magnet (/lp/kalorienrechner, 15.10.2026) — öffentliche Seite nach dem Mockup
   docs/ernaehrung/mockups/LpKalorienrechner.dc.html (helle Fläche, dunkle Ergebniskarte, P1-Orange). Das CRM-Design
   (crm-neu) gilt hier nicht. Gerechnet wird NUR auf dem Server (dieselbe Formel wie App und CRM); die Seite zeigt das
   Ergebnis sofort und schickt es auf Wunsch per E-Mail — dafür entsteht ein Lead mit Herkunft. Unter 18: kein Ergebnis. */

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api/public/p1`;
const feld = 'block w-full mt-1.5 rounded-[10px] border border-[#ddd5ca] bg-white px-3 py-3 text-base text-[#1a1714] focus:outline-none focus:border-[#ef7c23]';
const wahl = (an) => `rounded-[10px] border px-3 py-3 text-sm font-bold transition-colors ${an ? 'bg-[#1a1714] text-[#fdfaf6] border-[#1a1714]' : 'bg-white text-[#1a1714] border-[#ddd5ca] hover:border-[#1a1714]'}`;

export default function KalorienrechnerPage() {
  useSeitenaufruf('kalorienrechner');
  const [k, setK] = useState(null);
  const [e, setE] = useState({ geschlecht: 'w', alter: '', groesse_cm: '', gewicht_kg: '', aktivitaet: 'wenig', ziel: 'abnehmen' });
  const [r, setR] = useState(null);
  const [hinweis, setHinweis] = useState('');
  const [busy, setBusy] = useState(false);
  const [m, setM] = useState({ vorname: '', email: '', consent: false, website: '' });
  const [gesendet, setGesendet] = useState(false);
  const [fehler, setFehler] = useState('');
  useEffect(() => { axios.get(`${API}/kalorienrechner`).then((x) => setK(x.data)).catch(() => setK(null)); }, []);

  const vollstaendig = e.alter && e.groesse_cm && e.gewicht_kg;
  const minderjaehrig = Number(e.alter) > 0 && Number(e.alter) < 18;
  const zahlen = () => ({ ...e, alter: Number(e.alter), groesse_cm: Number(String(e.groesse_cm).replace(',', '.')), gewicht_kg: Number(String(e.gewicht_kg).replace(',', '.')) });

  // Rechnen, sobald alle Angaben da sind — ohne Knopf, wie im Mockup
  useEffect(() => {
    if (!vollstaendig || minderjaehrig) { setR(null); setHinweis(''); return; }
    const t = setTimeout(async () => {
      setBusy(true);
      try { const x = await axios.post(`${API}/kalorienrechner/rechnen`, zahlen()); setR(x.data); setHinweis(''); }
      catch (err) { setR(null); setHinweis(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Bitte prüfe deine Angaben.'); }
      finally { setBusy(false); }
    }, 350);
    return () => clearTimeout(t);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [e.geschlecht, e.alter, e.groesse_cm, e.gewicht_kg, e.aktivitaet, e.ziel]);

  const senden = async (ev) => {
    ev.preventDefault();
    setFehler('');
    if (!m.consent) { setFehler('Bitte bestätige den Versand per E-Mail.'); return; }
    setBusy(true);
    try {
      const herkunft = Object.fromEntries([...new URLSearchParams(window.location.search)].filter(([key]) => /^(utm_|fbclid|gclid)/.test(key)));
      await axios.post(`${API}/kalorienrechner/anfrage`, { ...zahlen(), vorname: m.vorname.trim(), email: m.email.trim(), consent: m.consent, website: m.website, herkunft });
      setGesendet(true);
    } catch (err) { setFehler(typeof err?.response?.data?.detail === 'string' ? err.response.data.detail : 'Das hat nicht geklappt — bitte noch einmal versuchen.'); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#fdfaf6] text-[#1a1714]" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      <header className="mx-auto flex max-w-[1200px] items-center justify-between gap-4 px-6 py-4">
        <div className="flex items-center gap-2.5"><div className="flex h-10 w-10 items-center justify-center rounded-lg bg-[#1a1714] text-xl font-black text-[#ef7c23]">P1</div><div className="font-extrabold">P1 Performance Center</div></div>
        <a href="/landingpage" className="text-[15px] font-bold text-[#a8520f] hover:text-[#7a3b09]">Probetraining</a>
      </header>
      <main className="mx-auto flex max-w-[1200px] flex-wrap items-start gap-10 px-6 pb-16 pt-6">
        <section className="min-w-0 flex-[1_1_380px] pt-6">
          <span className="inline-block rounded-full bg-[#fdeedf] px-3 py-1.5 text-[13px] font-extrabold text-[#7a3b09]">Kostenlos · 60 Sekunden</span>
          <h1 className="mt-4 text-[40px] font-extrabold leading-[1.05] md:text-[48px]" style={{ textWrap: 'balance' }}>Wie viel solltest du wirklich essen?</h1>
          <p className="mt-4 max-w-[460px] text-lg leading-[1.55] text-[#3d3630]">Berechne deinen Kalorien- und Makrobedarf für dein Ziel. Das Ergebnis siehst du sofort — und bekommst es mit Tipps per E-Mail.</p>
          <ul className="mt-6 space-y-3 text-base">
            {['Kalorien für Abnehmen, Halten oder Aufbau', 'Protein, Kohlenhydrate und Fett in Gramm', 'Erstellt vom P1-Coachingteam in Pforzheim-Eutingen'].map((t) => (
              <li key={t} className="flex gap-2.5"><Check className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#a8520f]" /> {t}</li>
            ))}
          </ul>
        </section>

        <section aria-label="Kalorienrechner" className="min-w-0 flex-[1_1_460px] space-y-4 rounded-[20px] border border-[#ece6dd] bg-white p-6 shadow-[0_10px_30px_rgba(26,23,20,0.06)] md:p-7">
          <div>
            <div className="mb-2 text-sm font-bold">Geschlecht</div>
            <div className="grid grid-cols-3 gap-2">
              {[['w', 'Weiblich'], ['m', 'Männlich'], ['d', 'Divers']].map(([v, l]) => <button key={v} type="button" className={wahl(e.geschlecht === v)} onClick={() => setE({ ...e, geschlecht: v })}>{l}</button>)}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2.5">
            {[['alter', 'Alter', '1'], ['groesse_cm', 'Größe (cm)', '1'], ['gewicht_kg', 'Gewicht (kg)', '0.1']].map(([key, label, step]) => (
              <label key={key} className="text-sm font-bold">{label}<input type="number" inputMode="decimal" step={step} value={e[key]} onChange={(ev) => setE({ ...e, [key]: ev.target.value })} className={feld} /></label>
            ))}
          </div>
          <label className="block text-sm font-bold">Wie aktiv bist du?
            <select value={e.aktivitaet} onChange={(ev) => setE({ ...e, aktivitaet: ev.target.value })} className={feld}>
              {Object.entries(k?.aktivitaet || { kaum: 'Kaum Bewegung, Bürojob', wenig: '1–2× Training pro Woche', mittel: '3–4× Training pro Woche', hoch: '5× und mehr / körperliche Arbeit' }).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <div>
            <div className="mb-2 text-sm font-bold">Dein Ziel</div>
            <div className="grid grid-cols-3 gap-2">
              {Object.entries(k?.ziele || { abnehmen: 'Abnehmen', halten: 'Gewicht halten', aufbauen: 'Muskeln aufbauen' }).map(([v, l]) => <button key={v} type="button" className={wahl(e.ziel === v)} onClick={() => setE({ ...e, ziel: v })}>{l}</button>)}
            </div>
          </div>

          {minderjaehrig && <div className="rounded-[14px] bg-[#f3ede5] p-4 leading-relaxed">{k?.minderjaehrig || 'Für Jugendliche unter 18 rechnen wir keine Kalorienziele aus. Sprich uns gerne im Studio an.'}</div>}
          {hinweis && <p className="text-sm text-[#a8520f]" role="alert">{hinweis}</p>}
          {r && (
            <div className="rounded-2xl bg-[#1a1714] p-5 text-[#fdfaf6]" data-testid="kalorienrechner-ergebnis">
              <div className="text-[13px] text-[#cfc6bb]">Dein Tagesziel</div>
              <div><span className="text-5xl font-black text-[#ef7c23]" style={{ fontFamily: "'Anton', 'Inter', sans-serif" }}>{r.kcal.toLocaleString('de-DE')}</span><span className="text-[#cfc6bb]"> kcal</span></div>
              <div className="mt-2.5 grid grid-cols-3 gap-2">
                {[[r.eiweiss, 'Protein'], [r.kh, 'Kohlenhydrate'], [r.fett, 'Fett']].map(([v, l]) => <div key={l}><div className="text-[22px] font-black" style={{ fontFamily: "'Anton', 'Inter', sans-serif" }}>{v} g</div><div className="text-xs text-[#cfc6bb]">{l}</div></div>)}
              </div>
              <div className="mt-2.5 text-xs text-[#cfc6bb]">Verbrauch ca. {Number(r.verbrauch || 0).toLocaleString('de-DE')} kcal{r.floor_hinweis ? ` · ${r.floor_hinweis}` : ''}</div>
            </div>
          )}

          {r && !gesendet && (
            <form onSubmit={senden} className="space-y-2.5">
              <div className="font-extrabold">Ergebnis + Plan-Tipps per E-Mail</div>
              <div className="grid grid-cols-2 gap-2">
                <input type="text" placeholder="Vorname" required minLength={2} maxLength={60} value={m.vorname} onChange={(ev) => setM({ ...m, vorname: ev.target.value })} className={feld.replace('mt-1.5', '')} aria-label="Vorname" />
                <input type="email" placeholder="E-Mail" required value={m.email} onChange={(ev) => setM({ ...m, email: ev.target.value })} className={feld.replace('mt-1.5', '')} aria-label="E-Mail" />
              </div>
              <input type="text" value={m.website} onChange={(ev) => setM({ ...m, website: ev.target.value })} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />
              <label className="flex items-start gap-2 text-sm text-[#3d3630]"><input type="checkbox" checked={m.consent} onChange={(ev) => setM({ ...m, consent: ev.target.checked })} className="mt-1" /> {k?.einwilligung || 'Ich möchte Tipps und Angebote von P1 per E-Mail erhalten. Abmeldung jederzeit möglich.'} <a href="/datenschutz" className="underline">Datenschutz</a></label>
              {fehler && <p className="text-sm text-[#a8520f]" role="alert">{fehler}</p>}
              <button type="submit" disabled={busy} className="inline-flex h-12 w-full items-center justify-center gap-2 rounded-[12px] bg-[#ef7c23] px-5 text-base font-extrabold text-black hover:bg-[#f68f3f] disabled:opacity-60">{busy && <Loader2 className="h-4 w-4 animate-spin" />}Ergebnis zusenden</button>
            </form>
          )}
          {gesendet && <div className="rounded-[14px] bg-[#f3ede5] p-4 text-sm leading-relaxed"><strong>Unterwegs.</strong> Dein Ergebnis mit drei Tipps ist auf dem Weg in dein Postfach.</div>}

          {k?.abo && r && (
            <div className="rounded-[14px] border border-[#ece6dd] p-4 text-sm leading-relaxed">
              <div className="font-extrabold">Lust auf Begleitung?</div>
              Ernährungscoaching mit Philip ab {Number(k.abo.preis_monat).toLocaleString('de-DE', { maximumFractionDigits: 0 })} €/Monat, Mindestlaufzeit {k.abo.mindestlaufzeit_monate} Monate. Persönlich betreut, abgestimmt auf deine Trainingstage im P1. <a href="/landingpage" className="font-bold underline">Kostenloses Probetraining</a>
            </div>
          )}
        </section>
      </main>
    </div>
  );
}
